import React from 'react';
import { ProductProvider } from './context/ProductContext';
import { AuthProvider } from './components/auth/AuthContext';
import { MenuContainer } from './components/kitchen/menu/MenuContainer';
import { ProductsContainer } from './components/products/ProductsContainer';
import { StatisticsContainer } from './components/statistics/StatisticsContainer';
import { ShoppingListContainer } from './components/shopping/ShoppingListContainer';
import { Navigation } from './components/navigation/Navigation';
import { UserMenu } from './components/auth/UserMenu';
import { ChefHat, Globe, Linkedin } from 'lucide-react';

type View = 'menu' | 'products' | 'statistics' | 'shopping';

const creators = [
  {
    name: 'Víctor López Rapado',
    web: 'https://xictorlr.com',
    linkedin: 'https://www.linkedin.com/in/victorlopezrapado/',
    role: 'Desarrollo'
  },
  {
    name: 'Claudia Navascues Fernandez',
    web: 'https://claunavascues.com',
    linkedin: 'https://www.linkedin.com/in/claudia-navascues-fernandez/',
    role: 'Idea original'
  }
];

export default function App() {
  const [currentView, setCurrentView] = React.useState<View>('menu');

  return (
    <AuthProvider>
      <ProductProvider>
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
          <header className="bg-white shadow-md sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                  <ChefHat className="text-orange-500" size={32} />
                  <h1 className="text-3xl font-bold text-gray-900">AI Cocina Virtual</h1>
                </div>
                <div className="flex items-center gap-4">
                  <Navigation currentView={currentView} onViewChange={setCurrentView} />
                  <UserMenu />
                </div>
              </div>
            </div>
          </header>

          <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {currentView === 'menu' && <MenuContainer />}
            {currentView === 'products' && <ProductsContainer />}
            {currentView === 'statistics' && <StatisticsContainer />}
            {currentView === 'shopping' && <ShoppingListContainer />}
          </main>

          <footer className="bg-white mt-8 py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  {creators.map((creator, index) => (
                    <div key={index} className="flex flex-col items-center md:items-start">
                      <p className="text-gray-600 font-medium">
                        {creator.name} - {creator.role}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <a
                          href={creator.web}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-orange-500 hover:text-orange-600 font-medium inline-flex items-center gap-1 transition-colors"
                        >
                          <Globe size={16} />
                          Web
                        </a>
                        <span className="text-gray-300">|</span>
                        <a
                          href={creator.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1 transition-colors"
                        >
                          <Linkedin size={16} />
                          LinkedIn
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <ChefHat className="text-orange-500" size={20} />
                  <span className="text-gray-500">AI Cocina Virtual © 2024</span>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </ProductProvider>
    </AuthProvider>
  );
}